// @dynamic-i18n-keys
//   packagePicker.coreOffer.core_access.title
//   packagePicker.coreOffer.core_plus.title
//   packagePicker.coreOffer.core_pro.title
//   packagePicker.coreOffer.core_pro_plus.title
//   packagePicker.coreOffer.core_max.title
import type { FC } from 'react';
import { useState } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import camelCase from 'lodash/camelCase';
import { PapiProductV3, PapiSubscriptionV3 } from '@wix/da-papi-types';
import { formatDollars } from '@wix/da-shared-react/pkg/utils/formatting';
import UserSymbol from '@wix/da-shared-react/pkg/User/_partials/UserSymbol';
import ArrowDown from '@wix/da-ds/pkg/Icons/ArrowDown';
import ArrowUp from '@wix/da-ds/pkg/Icons/ArrowUp';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import BillingCyclePopup from '../../../Popup/BillingCyclePopup';
import BenefitsList from './_partials/BenefitsList';
import CardButton from './_partials/CardButton';
import { CoreTypes } from '../../../types';
import { type PackagePickerStoreSlice } from '../../../../../redux/packagePicker/types';
import {
  getPriceBeforeDiscountForPackagePickerV3,
  getPriceForPackagePickerV3,
  getRecommendedPackageType,
} from '../../../utils';
import { addSubproductIdToURL } from '../../../../../helpers/url';
import { packagesData } from '../../../constants';
import useOpenCPPopupOnIdParam from '../../../hooks/useOpenCPPopupOnIdParam';

import s from './MobileHeroCard.scss';

export interface Props {
  coreType: CoreTypes;
  packageProducts: PapiProductV3[];
  pickerMode: PackagePickerStoreSlice['pickerMode'];
  alreadyHasSubscription: boolean;
  currentCoreSubscription?: PapiSubscriptionV3;
  sale: PackagePickerStoreSlice['sale'];
  packageConfig?: PackagePickerStoreSlice['packageConfig'];
  packageBenefits?: PackagePickerStoreSlice['packageBenefits'];
  referer: string;
}

const MobileHeroCard: FC<Props> = ({
  coreType,
  packageProducts,
  pickerMode,
  packageConfig,
  packageBenefits,
  alreadyHasSubscription,
  currentCoreSubscription,
  sale,
  referer,
}) => {
  const { t } = useTranslation();
  const [selectedPackage, setSelectedPackage] = useState<number[]>([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const isOpenByDefault =
    (!alreadyHasSubscription && coreType === getRecommendedPackageType(packageConfig)) ||
    (alreadyHasSubscription &&
      currentCoreSubscription?.orderV3?.product.corePackage?.coreType === coreType);

  const [isOpen, setIsOpen] = useState(isOpenByDefault);
  const openBilling = () => setIsPopupOpen(true);

  const monthlyPackageProduct = packageProducts.find(
    p => p.corePackage?.coreType === coreType && p.corePackage?.interval === 'month'
  ) as PapiProductV3;
  const yearlyPackageProduct = packageProducts.find(
    p => p.corePackage?.coreType === coreType && p.corePackage?.interval === 'year'
  ) as PapiProductV3;

  const isPackageDisabled =
    packageConfig?.[camelCase(coreType)]?.enabled === false ||
    (monthlyPackageProduct.corePackage?.status === 'disabled' &&
      yearlyPackageProduct.corePackage?.status === 'disabled');

  // Discount might apply to monthly or yearly packages only, or both.
  const hasDiscount = Boolean(
    (yearlyPackageProduct.discount || monthlyPackageProduct.discount) && sale?.ppShowDiscount
  );
  const isGift = pickerMode === 'gift';

  const showCTA = isGift || !currentCoreSubscription || currentCoreSubscription.isUpgradable;

  useOpenCPPopupOnIdParam({
    monthlyPackageProduct,
    yearlyPackageProduct,
    setSelectedPackage,
    setIsPopupOpen,
  });

  const handleSelect = selected => {
    setSelectedPackage(selected);
    openBilling();

    addSubproductIdToURL(selected[0]);
  };

  const isCurrent =
    alreadyHasSubscription &&
    currentCoreSubscription?.orderV3?.product.corePackage?.coreType === coreType;

  const isRecommended = getRecommendedPackageType(packageConfig) === coreType;

  return (
    <div
      className={cn(s['root'], isRecommended && s['with-frame'], hasDiscount && s['with-discount'])}
    >
      <div className={cn(s['main'], (isCurrent || isRecommended) && s['with-tag'])}>
        {hasDiscount && (
          <div className={cn(s['sale-badge'], isRecommended && s['highlighted'])}>
            {t('packagePicker.coreOffer.saleBadge', {
              discountPercent: yearlyPackageProduct.discount
                ? yearlyPackageProduct.discount.amount.percent
                : monthlyPackageProduct.discount?.amount.percent,
            })}
          </div>
        )}
        {isCurrent && (
          <div className={cn(s['tag'], s['current-plan-tag'])}>
            {t('packagePicker.coreOffer.currentPlan')}
          </div>
        )}
        {isRecommended && !isCurrent && (
          <div className={s['tag']}>{t('packagePicker.coreOffer.popular')}</div>
        )}
        <div className={s['core-logo']}>
          <UserSymbol userSymbol={coreType} className={s['core-symbol']} />
        </div>
        <div className={s['title']}>{t(packagesData[coreType].title)}</div>
        <div className={s['price-wrapper']}>
          <div className={s['price']}>
            {formatDollars(
              getPriceForPackagePickerV3({
                monthlyPackageProduct,
                yearlyPackageProduct,
                showDiscount: !!sale?.ppShowDiscount,
              })
            )}
          </div>
          <div className={s['period']}>{t('packagePicker.coreOffer.perMonth')}</div>
        </div>
        {hasDiscount && (
          <div className={s['price-before']}>
            {formatDollars(
              getPriceBeforeDiscountForPackagePickerV3({
                monthlyPackageProduct,
                yearlyPackageProduct,
              })
            )}
          </div>
        )}

        {showCTA && (
          <CardButton
            className={s['button']}
            coreType={coreType}
            pickerMode={pickerMode}
            alreadyHasSubscription={alreadyHasSubscription}
            monthlyPackageProduct={monthlyPackageProduct}
            yearlyPackageProduct={yearlyPackageProduct}
            isRecommended={isRecommended}
            isCurrent={isCurrent}
            onSelect={handleSelect}
            isDisabled={isPackageDisabled}
          />
        )}

        <BenefitsList
          coreType={coreType}
          isOpen={isOpen}
          referer={referer}
          packageBenefits={packageBenefits}
        />

        <TextButton
          size={'small'}
          variant={'main'}
          postfix={isOpen ? ArrowUp : ArrowDown}
          onClick={() => setIsOpen(current => !current)}
        >
          {isOpen ? t('ponies.hero.showLess') : t('ponies.hero.showMore')}
        </TextButton>

        <BillingCyclePopup
          selectedPackage={selectedPackage}
          coreType={coreType}
          isOpen={isPopupOpen}
          onRequestClose={() => setIsPopupOpen(false)}
          withCloseButton
        />
      </div>
    </div>
  );
};
MobileHeroCard.displayName = 'MobileHeroCard';

export default MobileHeroCard;
